/* SIZESOFTX
=========================================================================================
* Descripción: Modal para búsqueda de artículo por foto con IA
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 25/04/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Cámara compatible con Android y permisos web
*      3        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Normalización de clave para selección por foto
*      4        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Carga de artículo por clave igual al buscador normal
*      5        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Respeta cámara seleccionada en pantalla móvil
*      6        | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Recorte normalizado con envoltura por click para búsqueda IA
*      7        | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Búsqueda automática al tocar el artículo sin botón de captura
*      8        | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Asignación de foto al artículo cargado desde el buscador
*      9        | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Encabezado fijo de resultados y limpieza de error al identificar
* ---------------------------------------------------------------------------------------
*/

import { useRef, useState, useEffect } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, Typography, CircularProgress, Card, CardContent, List, IconButton } from "@mui/material";
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useIAEntrenamiento } from "../../../context/IA/IAEntrenamientoContext";
import { useArticulos } from "../../../context/Catalogos/ArticulosContext";
import CameraObjectSelectorOverlay from "../CameraObjectSelectorOverlay";
import {
    captureVideoFrame,
    createObjectEnvelopeSelection,
    createObjectSelectionFromSeedRays,
    createTrackingTemplate,
    getCameraPoint,
    trackObjectSelection,
} from "../utils/cameraSelection";

export default function ModalBuscarPorFoto({ open, onClose, onArticuloSelect, useFrontCamera = false }) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const objectSelectionRef = useRef(null);
    const trackingTemplateRef = useRef(null);
    const trackingAnimationRef = useRef(null);
    const predictionRequestRef = useRef(0);
    const [loading, setLoading] = useState(false);
    const [predicciones, setPredicciones] = useState([]);
    const [error, setError] = useState(null);
    const [cameraStatus, setCameraStatus] = useState("idle");
    const [objectSelection, setObjectSelection] = useState(null);
    const userActivatedCameraRef = useRef(false);

    const {
        articuloSeleccionado,
        handlePredecir,
    } = useIAEntrenamiento();
    const { handleObtenerArticuloByClave } = useArticulos();

    const getClaveArticulo = (articulo) => {
        return articulo?.clavearticulo
            || articulo?.claveArticulo
            || articulo?.ClaveArticulo
            || articulo?.sClaveArticulo
            || articulo?.SClaveArticulo
            || "";
    };

    useEffect(() => {
        if (open) {
            if (userActivatedCameraRef.current) {
                iniciarCamara();
            }
        } else {
            detenerCamara();
            setPredicciones([]);
            setError(null);
            setObjectSelection(null);
            objectSelectionRef.current = null;
            trackingTemplateRef.current = null;
        }
        
        return () => detenerCamara();
    }, [open]);

    useEffect(() => {
        let lastTracking = 0;
        const ejecutarSeguimiento = (timestamp) => {
            if (
                videoRef.current
                && objectSelectionRef.current
                && trackingTemplateRef.current
                && timestamp - lastTracking > 85
            ) {
                const nextSelection = trackObjectSelection({
                    videoElement: videoRef.current,
                    currentSelection: objectSelectionRef.current,
                    template: trackingTemplateRef.current,
                    mirror: useFrontCamera,
                    searchRadiusPct: 0.045,
                    searchSteps: 3,
                });
                objectSelectionRef.current = nextSelection;
                setObjectSelection(nextSelection);
                lastTracking = timestamp;
            }
            trackingAnimationRef.current = requestAnimationFrame(ejecutarSeguimiento);
        };
        trackingAnimationRef.current = requestAnimationFrame(ejecutarSeguimiento);
        return () => {
            if (trackingAnimationRef.current) {
                cancelAnimationFrame(trackingAnimationRef.current);
            }
        };
    }, [useFrontCamera]);

    const detenerCamara = () => {
        const activeStream = streamRef.current || videoRef.current?.srcObject;
        if (activeStream) {
            activeStream.getTracks().forEach(track => track.stop());
        }
        streamRef.current = null;
        objectSelectionRef.current = null;
        trackingTemplateRef.current = null;
        setObjectSelection(null);
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraStatus("idle");
    };

    const getCameraErrorMessage = (cameraError) => {
        if (!navigator.mediaDevices?.getUserMedia) {
            return "Este navegador no permite abrir la cámara desde la web.";
        }
        if (!window.isSecureContext) {
            return "La cámara en Android requiere abrir SizeSoftX desde HTTPS o localhost.";
        }
        if (cameraError?.name === "NotAllowedError" || cameraError?.name === "PermissionDeniedError") {
            return "Permiso de cámara denegado. Active el permiso de cámara del navegador para SizeSoftX.";
        }
        if (cameraError?.name === "NotFoundError" || cameraError?.name === "DevicesNotFoundError") {
            return "No se encontró una cámara disponible en el dispositivo.";
        }
        if (cameraError?.name === "NotReadableError" || cameraError?.name === "TrackStartError") {
            return "La cámara está ocupada por otra aplicación o pestaña.";
        }
        return "No se pudo acceder a la cámara.";
    };

    const iniciarCamara = async () => {
        try {
            setCameraStatus("requesting");
            setError(null);

            if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) {
                throw new Error("CAMERA_UNAVAILABLE");
            }

            detenerCamara();

            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: useFrontCamera ? "user" : "environment" },
                    width: { ideal: 640 },
                    height: { ideal: 480 }
                }
            });
            streamRef.current = mediaStream;
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                await videoRef.current.play().catch(() => {});
            }
            setCameraStatus("ready");
            userActivatedCameraRef.current = true;
        } catch (err) {
            console.error("Error al iniciar cámara:", err);
            detenerCamara();
            setCameraStatus("error");
            setError(getCameraErrorMessage(err));
        }
    };

    const seleccionarObjetoBusqueda = async (event) => {
        if (!videoRef.current || cameraStatus !== "ready" || loading) return;
        event.preventDefault();

        const seedPoint = getCameraPoint(event, videoRef.current);
        const raySelection = createObjectSelectionFromSeedRays({
            videoElement: videoRef.current,
            seedPoint,
            mirror: useFrontCamera,
        });
        const verificationSelection = raySelection
            ? {
                ...createObjectEnvelopeSelection(raySelection, 0.045),
                debugRays: raySelection.debugRays,
            }
            : null;
        const trackingTemplate = verificationSelection
            ? createTrackingTemplate({
                videoElement: videoRef.current,
                selection: verificationSelection,
                mirror: useFrontCamera,
            })
            : null;
        const selectionWithTracking = verificationSelection && trackingTemplate?.radialDebugRays
            ? { ...verificationSelection, debugRays: trackingTemplate.radialDebugRays }
            : verificationSelection;

        objectSelectionRef.current = selectionWithTracking;
        trackingTemplateRef.current = trackingTemplate;
        setObjectSelection(selectionWithTracking);

        if (!selectionWithTracking) {
            setError("No se pudo generar la envoltura del artículo");
        } else {
            setError(null);
            await capturarYPredecir(selectionWithTracking);
        }
    };

    const capturarSeleccionActual = (selectionOverride = objectSelectionRef.current, quality = 0.82) => {
        if (!videoRef.current || !canvasRef.current || cameraStatus !== "ready" || !selectionOverride) {
            return null;
        }

        return captureVideoFrame({
            videoElement: videoRef.current,
            canvasElement: canvasRef.current,
            selection: selectionOverride,
            mirror: useFrontCamera,
            quality,
        });
    };

    const capturarYPredecir = async (selectionOverride = objectSelectionRef.current) => {
        if (!videoRef.current || !canvasRef.current || cameraStatus !== "ready") return;
        if (!selectionOverride) {
            setError("Toque el artículo para generar la envoltura de búsqueda");
            return;
        }

        const imagenBase64 = capturarSeleccionActual(selectionOverride, 0.8);
        if (!imagenBase64) return;
        const requestId = predictionRequestRef.current + 1;
        predictionRequestRef.current = requestId;
        
        setLoading(true);
        setError(null);
        
        try {
            
            const resultado = await handlePredecir(imagenBase64);
            if (requestId !== predictionRequestRef.current) return;
            
            if (resultado) {
                setError(null);
                setPredicciones(prev => [resultado, ...prev]);
            } else {
                setError("No se pudo identificar el artículo");
            }
        } catch (err) {
            if (requestId !== predictionRequestRef.current) return;
            console.error("Error en captura:", err);
            setError("Error al analizar la imagen: " + err.message);
        } finally {
            if (requestId === predictionRequestRef.current) {
                setLoading(false);
            }
        }
    };

    const handleSeleccionarArticulo = async (prediccion) => {
        const clave = getClaveArticulo(prediccion);
        if (clave) {
            const articulo = await handleObtenerArticuloByClave(clave, false);
            const imagen = objectSelectionRef.current
                ? capturarSeleccionActual(objectSelectionRef.current, 0.82)
                : null;
            if (onArticuloSelect) {
                await onArticuloSelect(articulo || { clavearticulo: clave }, imagen);
            }
            onClose();
        }
    };

    const handleEliminarPrediccion = (index) => {
        setPredicciones(prev => prev.filter((_, i) => i !== index));
    };

    const handleLimpiarPredicciones = () => {
        setPredicciones([]);
        setError(null);
    };

    const handleCerrar = () => {
        detenerCamara();
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleCerrar}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    Buscar Artículo por Foto
                </Typography>
                <Button onClick={handleCerrar} startIcon={<CloseIcon />}>
                    Cerrar
                </Button>
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                    <Box sx={{ flex: 1 }}>
                        <Box
                            onPointerDown={seleccionarObjetoBusqueda}
                            sx={{
                                position: 'relative',
                                bgcolor: '#000',
                                borderRadius: 1,
                                overflow: 'hidden',
                                mb: 2,
                                aspectRatio: '4/3',
                                cursor: cameraStatus === "ready" ? "pointer" : "default",
                                touchAction: "none",
                                userSelect: "none",
                            }}
                        >
                            <video 
                                ref={videoRef} 
                                autoPlay 
                                playsInline 
                                muted
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transform: useFrontCamera ? 'scaleX(-1)' : 'none'
                                }}
                            />
                            <CameraObjectSelectorOverlay
                                selection={objectSelection}
                                disabled={cameraStatus !== "ready"}
                                videoElement={videoRef.current}
                                label="Artículo IA"
                                instructionLabel="Toque el artículo para buscar"
                            />

                            {cameraStatus !== "ready" && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexDirection: "column",
                                        gap: 1,
                                        p: 2,
                                        color: "white",
                                        textAlign: "center",
                                        bgcolor: "rgba(0,0,0,.72)",
                                    }}
                                >
                                    {cameraStatus === "requesting" ? (
                                        <>
                                            <CircularProgress size={28} sx={{ color: "white" }} />
                                            <Typography variant="body2">
                                                Solicitando permiso de cámara...
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                Cámara no activa
                                            </Typography>
                                            {error && (
                                                <Typography variant="caption">
                                                    {error}
                                                </Typography>
                                            )}
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<VideocamIcon />}
                                                onClick={iniciarCamara}
                                                disabled={cameraStatus === "requesting"}
                                            >
                                                Activar cámara
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            )}
                            
                            {loading && (
                                <Box sx={{ 
                                    position: 'absolute', 
                                    top: '50%', 
                                    left: '50%', 
                                    transform: 'translate(-50%, -50%)',
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                    p: 3,
                                    borderRadius: 2
                                }}>
                                    <CircularProgress sx={{ color: 'white' }} />
                                    <Typography sx={{ color: 'white', mt: 1 }}>
                                        Analizando...
                                    </Typography>
                                </Box>
                            )}
                            
                            <canvas ref={canvasRef} style={{ display: 'none' }} />
                        </Box>

                    </Box>

                    <Box sx={{ flex: 1, maxHeight: 400, minHeight: 260, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {error && (
                            <Card sx={{ bgcolor: '#ffebee', mb: 1, flexShrink: 0 }}>
                                <CardContent>
                                    <Typography color="error">{error}</Typography>
                                </CardContent>
                            </Card>
                        )}

                        {predicciones.length > 0 && (
                            <Card sx={{ mb: 1, flexShrink: 0 }}>
                                <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Resultados ({predicciones.length})
                                        </Typography>
                                        <Button 
                                            size="small" 
                                            color="error"
                                            onClick={handleLimpiarPredicciones}
                                            startIcon={<DeleteIcon />}
                                        >
                                            Limpiar
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}

                        <List sx={{ width: '100%', flex: 1, overflow: 'auto', pt: 0 }}>
                            {predicciones.map((prediccion, index) => {
                                const confianza = prediccion?.confianza ? (Number(prediccion.confianza) * 100).toFixed(1) : "0";
                                const clave = getClaveArticulo(prediccion) || "N/A";
                                const mensaje = prediccion?.mensaje || "Artículo identificado";
                                return (
                                    <Card 
                                        key={index} 
                                        sx={{ 
                                            mb: 1,
                                            bgcolor: Number(confianza) >= 75 ? '#e8f5e9' : '#fff3e0',
                                            border: '2px solid',
                                            borderColor: Number(confianza) >= 75 ? '#4caf50' : '#ff9800'
                                        }}
                                    >
                                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <CheckCircleIcon 
                                                            color={Number(confianza) >= 75 ? "success" : "warning"} 
                                                            sx={{ mr: 0.5, fontSize: 18 }} 
                                                        />
                                                        <Typography variant="subtitle2" fontWeight="bold">
                                                            {clave}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                                        {mensaje}
                                                    </Typography>
                                                    <Typography 
                                                        variant="body2" 
                                                        color={Number(confianza) >= 75 ? "success.main" : "warning.main"}
                                                        fontWeight="bold"
                                                    >
                                                        Confianza: {confianza}%
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Button 
                                                        size="small" 
                                                        variant="contained" 
                                                        color="success"
                                                        onClick={() => handleSeleccionarArticulo(prediccion)}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                    <IconButton 
                                                        size="small" 
                                                        color="error"
                                                        onClick={() => handleEliminarPrediccion(index)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </List>

                        {predicciones.length === 0 && !loading && !error && (
                            <Card sx={{ bgcolor: '#f5f5f5', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <CameraAltIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Toque el artículo en la cámara para identificarlo
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Los resultados aparecerán aquí
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
